<template>
  <div class="host-game-loader">

    <!-- Loading State -->
    <div v-if="gameState === 'loading'" class="loading-container">
      <div class="text-center">
        <div class="spinner-border text-primary mb-3" role="status">
          <span class="visually-hidden">Loading game...</span>
        </div>
        <h4>Loading {{ gameType }} Game</h4>
        <p class="text-muted">Preparing host interface...</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-if="gameState === 'error'" class="error-container">
      <div class="alert alert-danger">
        <h4><i class="bi bi-exclamation-triangle"></i> Game Load Error</h4>
        <p>{{ errorMessage }}</p>
        <button @click="retryLoad" class="btn btn-outline-danger">
          <i class="bi bi-arrow-clockwise"></i> Retry
        </button>
      </div>
    </div>

    <!-- Host Game Interface Container -->
    <div v-if="gameState === 'loaded'" class="host-game-container">

      <!-- Quiz Host Interface -->
      <QuizHostInterface
        v-if="gameType === 'basic-quiz'"
        :quiz-config="gameConfig"
        :connected-players="connectedPlayers"
        :signaling="signaling"
        :host-signaling="hostSignaling"
        :current-screen-index="currentScreenIndex"
        :game-status="gameStatus"
        :player-answers="playerAnswers"
        :player-scores="playerScores"
        @screen-changed="handleScreenChanged"
        @quiz-started="handleQuizStarted"
        @quiz-paused="handleQuizPaused"
        @quiz-resumed="handleQuizResumed"
        @quiz-stopped="handleQuizStopped"
        @timer-skipped="handleTimerSkipped"
        @reveal-answers="handleRevealAnswers"
        ref="quizHostInterface"
      />

      <!-- Drag-Match Host Interface -->
      <DragMatchHostInterface
        v-if="gameType === 'drag-and-match'"
        :drag-match-config="gameConfig"
        :connected-players="connectedPlayers"
        :signaling="signaling"
        :host-signaling="hostSignaling"
        :current-screen-index="currentScreenIndex"
        :game-status="gameStatus"
        :player-answers="playerAnswers"
        :player-scores="playerScores"
        @screen-changed="handleScreenChanged"
        @game-started="handleGameStarted"
        @game-paused="handleGamePaused"
        @game-resumed="handleGameResumed"
        @game-stopped="handleGameStopped"
        @reveal-answers="handleRevealAnswers"
        ref="dragMatchHostInterface"
      />

      <!-- Other Game Types placeholder -->
      <div v-else-if="gameType === 'who-is-who'" class="coming-soon">
        <div class="alert alert-info">
          <h4><i class="bi bi-info-circle"></i> Coming Soon</h4>
          <p>"Who's Who" host interface is coming soon!</p>
        </div>
      </div>

      <!-- Unknown Game Type -->
      <div v-else class="unknown-game">
        <div class="alert alert-warning">
          <h4><i class="bi bi-question-circle"></i> Unknown Game</h4>
          <p>Game type "{{ gameType }}" is not supported for hosting.</p>
        </div>
      </div>

    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import QuizHostInterface from './QuizHostInterface.vue'
import DragMatchHostInterface from './DragMatchHostInterface.vue'
import { QuizConfig } from '@/games/basic-quiz/models/QuizConfig'
import type { DragMatchConfig } from '@/games/drag-match/models/DragMatchConfig'

// Props
interface Props {
  gameData: any // Game configuration from setup
  connectedPlayers: any[]
  signaling: any
  hostSignaling?: any
  currentScreenIndex?: number
  activeGameIndex?: number
}

const props = withDefaults(defineProps<Props>(), {
  currentScreenIndex: 0,
  activeGameIndex: null
})

// Emits
const emit = defineEmits<{
  'game-started': [gameType: string, config: any]
  'game-ended': [gameType: string]
  'screen-changed': [screenIndex: number]
  'player-action-required': [action: any]
  'broadcast-to-players': [message: any]
  'broadcast-to-tv': [message: any]
  'answers-revealed': [screenIndex: number, correctAnswers: any]
}>()

// Reactive state
const gameState = ref<'loading' | 'loaded' | 'error'>('loading')
const gameType = ref<string>('')
const gameConfig = ref<any>(null)
const errorMessage = ref<string>('')
const quizHostInterface = ref<InstanceType<typeof QuizHostInterface> | null>(null)
const dragMatchHostInterface = ref<InstanceType<typeof DragMatchHostInterface> | null>(null)

// Computed
const isQuizGame = computed(() => gameType.value === 'basic-quiz')
const isDragMatchGame = computed(() => gameType.value === 'drag-and-match')

// Pass through game state from parent components
const currentScreenIndex = computed(() => props.currentScreenIndex || 0)
const gameStatus = computed(() => {
  // Determine game status from the activeGameIndex prop
  return props.activeGameIndex !== null ? 'playing' : 'stopped'
})

// For now, initialize empty state for player answers and scores
// These should be fed from the actual game state in a future update
const playerAnswers = computed(() => new Map())
const playerScores = computed(() => new Map())

// Pass through host signaling
const hostSignaling = computed(() => props.hostSignaling)

// Methods
async function loadGame() {
  try {
    gameState.value = 'loading'
    errorMessage.value = ''

    console.log('Loading host game interface with data:', props.gameData)

    // Extract game type from data
    if (!props.gameData) {
      throw new Error('No game data provided')
    }

    // Determine game type
    if (props.gameData.gameplaySlug) {
      gameType.value = props.gameData.gameplaySlug
    } else if (props.gameData.type) {
      gameType.value = props.gameData.type
    } else {
      throw new Error('No game type specified in game data')
    }

    console.log(`Loading host interface for game type: ${gameType.value}`)

    // Load game-specific configuration
    if (gameType.value === 'basic-quiz') {
      await loadQuizHostGame()
    } else if (gameType.value === 'drag-and-match') {
      await loadDragMatchHostGame()
    } else {
      throw new Error(`Unsupported game type for hosting: ${gameType.value}`)
    }

    gameState.value = 'loaded'
    console.log('Host game interface loaded successfully')

  } catch (error) {
    console.error('Failed to load host game interface:', error)
    errorMessage.value = error instanceof Error ? error.message : 'Unknown error'
    gameState.value = 'error'
  }
}

async function loadQuizHostGame() {
  // Validate quiz configuration
  if (!props.gameData.screens || !Array.isArray(props.gameData.screens)) {
    throw new Error('Invalid quiz configuration: missing screens')
  }

  // Create quiz config object
  const quizConfig: QuizConfig = {
    gameplaySlug: props.gameData.gameplaySlug || 'basic-quiz',
    name: props.gameData.name || 'Quiz Game',
    config: props.gameData.config || {
      timeLimit: props.gameData.timeLimit,
      showHints: props.gameData.showHints || false
    },
    assets: props.gameData.assets || [],
    screens: props.gameData.screens,
    nextScreenTrigger: props.gameData.nextScreenTrigger || 'host',
    timerDuration: props.gameData.timerDuration || 0,
    scoreVisibility: props.gameData.scoreVisibility || 'real-time',
    correctAnswerVisibility: props.gameData.correctAnswerVisibility || 'after-answer',
    defaultPointValue: props.gameData.defaultPointValue || 10
  }

  // Validate screens
  for (const screen of quizConfig.screens) {
    if (!screen.screenType || !['interlude', 'question'].includes(screen.screenType)) {
      throw new Error(`Invalid screen type: ${screen.screenType}`)
    }

    if (screen.screenType === 'question' && (!screen.options || !Array.isArray(screen.options))) {
      throw new Error('Question screens must have options array')
    }
  }

  gameConfig.value = quizConfig
  console.log('Quiz host configuration loaded:', quizConfig)
}

async function loadDragMatchHostGame() {
  // Validate drag-match configuration
  if (!props.gameData.screens || !Array.isArray(props.gameData.screens)) {
    throw new Error('Invalid drag-match configuration: missing screens')
  }

  if (!props.gameData.assets || !Array.isArray(props.gameData.assets)) {
    throw new Error('Invalid drag-match configuration: missing assets')
  }

  // Create drag-match config object
  const dragMatchConfig: DragMatchConfig = {
    gameplaySlug: 'drag-and-match',
    name: props.gameData.name || 'Drag-Match Game',
    assets: props.gameData.assets,
    screens: props.gameData.screens,
    settings: props.gameData.settings || {}
  }

  // Validate screens
  for (const screen of dragMatchConfig.screens) {
    if (!screen.screenType || !['interlude', 'question'].includes(screen.screenType)) {
      throw new Error(`Invalid screen type: ${screen.screenType}`)
    }

    if (screen.screenType === 'question') {
      if (!screen.options || !Array.isArray(screen.options)) {
        throw new Error('Drag-match question screens must have options array')
      }
      if (!screen.slots || !Array.isArray(screen.slots)) {
        throw new Error('Drag-match question screens must have slots array')
      }
    }
  }

  gameConfig.value = dragMatchConfig
  console.log('Drag-match host configuration loaded:', dragMatchConfig)
}

function retryLoad() {
  loadGame()
}

// Event handlers from quiz host interface
function handleScreenChanged(screenIndex: number) {
  console.log(`Host: Screen changed to ${screenIndex}`)

  // Get the screen data
  const screenData = gameConfig.value?.screens?.[screenIndex]
  if (screenData) {
    // Broadcast to all players
    const playerMessage = {
      type: 'quiz-screen-change',
      screenIndex,
      screenData,
      timestamp: Date.now()
    }

    // Broadcast to TV view
    const tvMessage = {
      type: 'screen-changed',
      screenIndex,
      screenData,
      timestamp: Date.now()
    }

    emit('broadcast-to-players', playerMessage)
    emit('broadcast-to-tv', tvMessage)
    emit('screen-changed', screenIndex)
  }
}

function handleQuizStarted() {
  console.log('Host: Quiz started')

  const startMessage = {
    type: 'quiz-started',
    config: gameConfig.value,
    timestamp: Date.now()
  }

  emit('broadcast-to-players', startMessage)
  emit('broadcast-to-tv', startMessage)
  emit('game-started', gameType.value, gameConfig.value)
}

function handleQuizPaused() {
  console.log('Host: Quiz paused')

  const pauseMessage = {
    type: 'quiz-paused',
    timestamp: Date.now()
  }

  emit('broadcast-to-players', pauseMessage)
  emit('broadcast-to-tv', pauseMessage)
}

function handleQuizResumed() {
  console.log('Host: Quiz resumed')

  const resumeMessage = {
    type: 'quiz-resumed',
    timestamp: Date.now()
  }

  emit('broadcast-to-players', resumeMessage)
  emit('broadcast-to-tv', resumeMessage)
}

function handleQuizStopped() {
  console.log('Host: Quiz stopped')

  const stopMessage = {
    type: 'quiz-stopped',
    timestamp: Date.now()
  }

  emit('broadcast-to-players', stopMessage)
  emit('broadcast-to-tv', stopMessage)
  emit('game-ended', gameType.value)
}

function handleTimerSkipped() {
  console.log('Host: Timer skipped')

  const skipMessage = {
    type: 'quiz-timer-skipped',
    timestamp: Date.now()
  }

  emit('broadcast-to-players', skipMessage)
  emit('broadcast-to-tv', skipMessage)
}

function handleRevealAnswers(screenIndex: number, correctAnswers: any) {
  console.log('Host: Requesting reveal answers for current screen')

  // Server will use party state to determine current screen and correct answers
  if (props.hostSignaling?.sendHostMessage) {
    props.hostSignaling.sendHostMessage('reveal-answers', {})
  } else if (props.signaling?.send) {
    // Fallback to direct signaling with host-message wrapper
    props.signaling.send({
      type: 'host-message',
      data: {
        type: 'reveal-answers'
      }
    })
  } else {
    console.warn('Neither hostSignaling nor signaling available, cannot send reveal-answers message')
  }

  // Also emit to parent components for local handling if needed
  emit('answers-revealed', screenIndex, correctAnswers)
}

// Drag-Match specific event handlers
function handleGameStarted() {
  console.log('Host: Drag-match game started')

  const startMessage = {
    type: 'drag-match-started',
    config: gameConfig.value,
    timestamp: Date.now()
  }

  emit('broadcast-to-players', startMessage)
  emit('broadcast-to-tv', startMessage)
  emit('game-started', gameType.value, gameConfig.value)
}

function handleGamePaused() {
  console.log('Host: Drag-match game paused')

  const pauseMessage = {
    type: 'drag-match-paused',
    timestamp: Date.now()
  }

  emit('broadcast-to-players', pauseMessage)
  emit('broadcast-to-tv', pauseMessage)
}

function handleGameResumed() {
  console.log('Host: Drag-match game resumed')

  const resumeMessage = {
    type: 'drag-match-resumed',
    timestamp: Date.now()
  }

  emit('broadcast-to-players', resumeMessage)
  emit('broadcast-to-tv', resumeMessage)
}

function handleGameStopped() {
  console.log('Host: Drag-match game stopped')

  const stopMessage = {
    type: 'drag-match-stopped',
    timestamp: Date.now()
  }

  emit('broadcast-to-players', stopMessage)
  emit('broadcast-to-tv', stopMessage)
  emit('game-ended', gameType.value)
}

// Public API for external control
function startGame() {
  if (quizHostInterface.value && isQuizGame.value) {
    quizHostInterface.value.startQuiz()
  } else if (dragMatchHostInterface.value && isDragMatchGame.value) {
    dragMatchHostInterface.value.startGame()
  }
}

function pauseGame() {
  if (quizHostInterface.value && isQuizGame.value) {
    quizHostInterface.value.pauseQuiz()
  } else if (dragMatchHostInterface.value && isDragMatchGame.value) {
    dragMatchHostInterface.value.pauseGame()
  }
}

function resumeGame() {
  if (quizHostInterface.value && isQuizGame.value) {
    quizHostInterface.value.resumeQuiz()
  } else if (dragMatchHostInterface.value && isDragMatchGame.value) {
    dragMatchHostInterface.value.resumeGame()
  }
}

function stopGame() {
  if (quizHostInterface.value && isQuizGame.value) {
    quizHostInterface.value.stopQuiz()
  } else if (dragMatchHostInterface.value && isDragMatchGame.value) {
    dragMatchHostInterface.value.stopGame()
  }
}

function nextScreen() {
  if (quizHostInterface.value && isQuizGame.value) {
    quizHostInterface.value.nextScreen()
  } else if (dragMatchHostInterface.value && isDragMatchGame.value) {
    dragMatchHostInterface.value.nextScreen()
  }
}

function previousScreen() {
  if (quizHostInterface.value && isQuizGame.value) {
    quizHostInterface.value.previousScreen()
  } else if (dragMatchHostInterface.value && isDragMatchGame.value) {
    dragMatchHostInterface.value.previousScreen()
  }
}

function goToScreen(screenIndex: number) {
  if (quizHostInterface.value && isQuizGame.value) {
    quizHostInterface.value.goToScreen(screenIndex)
  } else if (dragMatchHostInterface.value && isDragMatchGame.value) {
    dragMatchHostInterface.value.goToScreen(screenIndex)
  }
}

function updatePlayerAnswer(playerId: string, answer: any) {
  if (quizHostInterface.value && isQuizGame.value) {
    quizHostInterface.value.updatePlayerAnswer(playerId, answer)
  } else if (dragMatchHostInterface.value && isDragMatchGame.value) {
    dragMatchHostInterface.value.updatePlayerScore(playerId, answer.score)
  }
}

function updatePlayerScore(playerId: string, score: number) {
  if (quizHostInterface.value && isQuizGame.value) {
    quizHostInterface.value.updatePlayerScore(playerId, score)
  } else if (dragMatchHostInterface.value && isDragMatchGame.value) {
    dragMatchHostInterface.value.updatePlayerScore(playerId, score)
  }
}

function getCurrentGameData() {
  if (quizHostInterface.value && isQuizGame.value) {
    return quizHostInterface.value.getCurrentScreenData()
  } else if (dragMatchHostInterface.value && isDragMatchGame.value) {
    return dragMatchHostInterface.value.getCurrentScreenData()
  }
  return null
}

// Expose methods for parent components
defineExpose({
  startGame,
  pauseGame,
  resumeGame,
  stopGame,
  nextScreen,
  previousScreen,
  goToScreen,
  updatePlayerAnswer,
  updatePlayerScore,
  getCurrentGameData,
  loadGame
})

// Lifecycle
onMounted(() => {
  console.log('Host game loader mounted with game data:', props.gameData)
  loadGame()
})

// Watch for game data changes
watch(() => props.gameData, () => {
  if (props.gameData) {
    loadGame()
  }
}, { deep: true })

// Watch for player changes to update quiz interface
watch(() => props.connectedPlayers, () => {
  console.log('Connected players updated:', props.connectedPlayers.length)
}, { deep: true })
</script>

<style scoped>
.host-game-loader {
  width: 100%;
  height: 100%;
}

.loading-container,
.error-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 40px 20px;
}

.host-game-container {
  width: 100%;
  height: 100%;
}

.coming-soon,
.unknown-game {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  padding: 40px 20px;
}

/* Loading animation */
.spinner-border {
  width: 3rem;
  height: 3rem;
}

/* Error state styling */
.error-container .alert {
  max-width: 500px;
  text-align: center;
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .loading-container,
  .error-container,
  .coming-soon,
  .unknown-game {
    min-height: 250px;
    padding: 20px 15px;
  }
}
</style>
